/**
 * Build getFrameAtTime for the pose overlay from swing_runs.pose_data.
 * Used by the canonical analysis page (/analysis/:id) to show skeleton overlay on video.
 */

import type { PoseData } from '@/features/telemetry2/types';

export type LandmarkFrame = {
  t: number;
  width: number;
  height: number;
  pose?: {
    landmarks?: Array<[number, number, number, number?]>;
  };
  hands?: {
    landmarks?: Array<[number, number, number, number?]>;
  };
};

/**
 * Build a sync getFrameAtTime from pose_data for OverlayCanvas.
 * Returns nearest frame by timestamp (no interpolation).
 */
export function createGetFrameAtTimeFromPoseData(poseData: PoseData | null): (timeSec: number) => LandmarkFrame | null {
  if (!poseData?.landmarks?.length) {
    return () => null;
  }

  const frames = poseData.landmarks
    .filter((f) => f.pose_landmarks != null)
    .sort((a, b) => a.timestamp_ms - b.timestamp_ms);

  if (frames.length === 0) return () => null;

  const meta = poseData.meta ?? {};
  const width = meta.width ?? 1920;
  const height = meta.height ?? 1080;

  return function getFrameAtTime(timeSec: number): LandmarkFrame | null {
    const targetMs = timeSec * 1000;
    let left = 0;
    let right = frames.length - 1;
    let nearest = 0;
    let minDiff = Infinity;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const diff = Math.abs(frames[mid].timestamp_ms - targetMs);
      if (diff < minDiff) {
        minDiff = diff;
        nearest = mid;
      }
      if (frames[mid].timestamp_ms < targetMs) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    const frame = frames[nearest];
    const pose_landmarks = frame.pose_landmarks;
    if (!pose_landmarks?.length) return null;

    const landmarks: Array<[number, number, number, number?]> = pose_landmarks.map((lm) => [
      lm.x,
      lm.y,
      lm.z,
      lm.visibility,
    ]);

    // OverlayCanvas expects pose.landmarks as array-of-persons: landmarks[0] = first person's 33 points
    return {
      t: frame.timestamp_ms / 1000,
      width,
      height,
      pose: { landmarks: [landmarks] },
    };
  };
}
